import { Injectable } from '@angular/core';
import { IdentityJwtPayload, IdentityToken, Nullable } from '../types/internal-types';
import { AccessToken, RefreshToken } from '../types/global-types';
import { TokenHelper } from '../helpers/token.helper';
import { InvalidTokenError } from 'jwt-decode';
import { TokenKey } from '../types/internal-enums';
import { LogHelper } from '../helpers/log.helper';
import { BehaviorSubject } from 'rxjs';
import { OptionsService } from './options.service';

@Injectable({ providedIn: 'root' })
export class TokenService {

  constructor(private readonly os :OptionsService) {
  }
  readonly accessToken: BehaviorSubject<Nullable<AccessToken>> = new BehaviorSubject<Nullable<AccessToken>>(null);
  readonly refreshToken: BehaviorSubject<Nullable<RefreshToken>> = new BehaviorSubject<Nullable<RefreshToken>>(null);

  private issuers: string[] = [];

  setAccessToken(token: unknown):AccessToken {
    return this.updateAccessToken<AccessToken>(this.setToken<AccessToken>(TokenKey.accessToken, token));
  }

  getAccessToken():AccessToken {
    return this.updateAccessToken(this.getToken(TokenKey.accessToken));
  }

  setRefreshToken(token: unknown):RefreshToken {
    return this.updateRefreshToken<RefreshToken>(this.setToken(TokenKey.refreshToken, token));
  }

  getRefreshToken():RefreshToken {
    return this.updateRefreshToken(this.getToken(TokenKey.refreshToken));
  }

  clearTokens():void {
    localStorage.removeItem(TokenKey.accessToken);
    localStorage.removeItem(TokenKey.refreshToken);

    this.updateAccessToken(null);
    this.updateRefreshToken(null);
  }

  private updateAccessToken<T extends Nullable<AccessToken>>(newToken: Nullable<AccessToken>): T {
    if (this.accessToken.value?.token !== newToken?.token) {
      this.accessToken.next(newToken);
    }

    return <T>this.accessToken.value;
  }

  private updateRefreshToken<T extends Nullable<RefreshToken>>(newToken: Nullable<RefreshToken>): T {
    if (this.refreshToken.value?.token !== newToken?.token) {
      this.refreshToken.next(newToken);
    }

    return <T>this.refreshToken.value;
  }

  private setToken<T extends IdentityToken>(key: TokenKey, token: unknown): T {
    /**
     * make sure the provided token is at least a string
     */
    if (typeof token !== 'string') {
      throw new InvalidTokenError(`Provided ${key} value is not a string`);
    }

    /**
     * get identity token
     */
    const identityToken:T = this.wrapToken<T>(token);

    /**
     * if provided - save it to local storage
     */
    if (identityToken) {
      localStorage.setItem(key, token);
    }

    LogHelper.log(key, identityToken);

    return identityToken;
  }

  private getToken<T extends IdentityToken>(key: TokenKey): Nullable<T> {
    /**
     * get token value from local storage or use null
     */
    const actualToken:string = localStorage.getItem(key) ?? null;

    if (typeof actualToken === 'string') {
      try {
        return this.wrapToken<T>(actualToken);
      } catch (error) {
        LogHelper.error(error);
      }
    }

    return null;
  }

  private wrapToken<T extends IdentityToken>(token: string): T {
    /**
     * parse a token and get its payload
     * NOTE: errors will be thrown if the token is expired or has invalid claims
     */
    const payload:IdentityJwtPayload|AccessToken = TokenHelper.getPayload(
      token,
      {
        iss: this.getIssuers(),
        //sub: this.os.options.application,
      },
      60
    );

    /**
     * return identity token object
     */
    return <T>{ token, payload: payload };
  }

  private getIssuers(): string[] {
    /**
     * if we already have issuers - ise them
     */
    if (this.issuers.length) {
      return this.issuers;
    }

    /**
     * get issuer value from the options
     */
    const issuer:string = this.os.options.serviceIssuer ?? this.os.options.backendUrl;

    this.issuers = [issuer];

    /**
     * make sure we have both variants (with and without "/api" suffix) as issuers
     * todo "/api" suffix verification must be removed when "/api" suffix will be removed from the "issuer" in IDMS
     */
    if (issuer.endsWith('/api')) {
      this.issuers.push(issuer.substring(0, issuer.lastIndexOf('/api')));
    } else {
      this.issuers.push(`${issuer}/api`);
    }

    return this.issuers;
  }
}
